import consumerCatalogJson from "./generated/action-consumer-catalog.v1.json" with {
  type: "json",
}
import type {
  ProjectedActionConsumer,
  RuntimeActionConsumerCatalogProjection,
} from "@cogo/schema/action-consumer-catalog"

export interface ActionConsumerCatalogReader {
  getActionConsumerContract(actionOrAlias: string): ProjectedActionConsumer | undefined
  listRuntimeActionAliases(): readonly string[]
}

export function assertActionConsumerCapabilityCoverage(
  runtimeCapabilityNames: readonly string[],
  catalog: RuntimeActionConsumerCatalogProjection,
): void {
  const capabilities = new Set(runtimeCapabilityNames)
  const catalogActions = new Set(catalog.actions.map((action) => action.action))
  const firstDifference = [...new Set([...capabilities, ...catalogActions])]
    .sort(compareText)
    .find((name) => capabilities.has(name) !== catalogActions.has(name))
  if (firstDifference) {
    throw new Error(`ACTION_CONSUMER_CAPABILITY_COVERAGE:${firstDifference}`)
  }
}

export function createActionConsumerCatalogReader(
  catalog: RuntimeActionConsumerCatalogProjection,
): ActionConsumerCatalogReader {
  const canonical = new Map<string, ProjectedActionConsumer>()
  for (const action of catalog.actions) {
    if (canonical.has(action.action)) {
      throw new Error(`ACTION_CONSUMER_CANONICAL_COLLISION:${action.action}`)
    }
    canonical.set(action.action, action)
  }

  const aliases = new Map<string, ProjectedActionConsumer>()
  for (const action of catalog.actions) {
    for (const alias of action.runtimeAliases) {
      if (canonical.has(alias) || aliases.has(alias)) {
        throw new Error(`ACTION_CONSUMER_ALIAS_COLLISION:${alias}`)
      }
      aliases.set(alias, action)
    }
  }
  const runtimeAliases = Object.freeze([...aliases.keys()].sort(compareText))
  return Object.freeze({
    getActionConsumerContract(actionOrAlias: string) {
      return canonical.get(actionOrAlias) ?? aliases.get(actionOrAlias)
    },
    listRuntimeActionAliases() {
      return runtimeAliases
    },
  })
}

export const RUNTIME_ACTION_CONSUMER_CATALOG = deepFreeze(
  structuredClone(consumerCatalogJson),
) as RuntimeActionConsumerCatalogProjection

const reader = createActionConsumerCatalogReader(RUNTIME_ACTION_CONSUMER_CATALOG)

export const getActionConsumerContract = reader.getActionConsumerContract
export const listRuntimeActionAliases = reader.listRuntimeActionAliases

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    for (const child of Object.values(value)) deepFreeze(child)
    Object.freeze(value)
  }
  return value
}

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0
}
